import { defineChain } from "viem";
import { arbitrum as vab, arbitrumGoerli as vabg, arbitrumSepolia as vabs, base as vbase, baseGoerli as vbaseg, baseSepolia as vbases, fraxtal as vfrax, goerli as vgo, mainnet as vmain, optimism as vop, optimismGoerli as vopg, optimismSepolia as vops, polygon as vpg, polygonAmoy as vpga, polygonMumbai as vpgm, sepolia as vsep, arbitrumNova as vabn, zora as vzora, zoraSepolia as vzoras, } from "viem/chains";
/**
 * Defines an Alchemy chain configuration by adding an Alchemy-specific RPC base URL to the chain's RPC URLs.
 *
 * @example
 * ```ts
 * import { defineAlchemyChain } from "@account-kit/infra";
 * import { sepolia } from "viem/chains";
 *
 * const chain = defineAlchemyChain({
 *  chain: sepolia,
 *  rpcBaseUrl: "https://eth-sepolia.g.alchemy.com/v2"
 * });
 * ```
 *
 * @param {AlchemyChainConfig} params The parameters for defining the Alchemy chain
 * @param {Chain} params.chain The original chain configuration
 * @param {string} params.rpcBaseUrl The Alchemy-specific RPC base URL
 * @returns {Chain} The updated chain configuration with the Alchemy RPC URL added
 */
export const defineAlchemyChain = ({ chain, rpcBaseUrl, }) => {
    return {
        ...chain,
        rpcUrls: {
            ...chain.rpcUrls,
            alchemy: {
                http: [rpcBaseUrl],
            },
        },
    };
};
export const arbitrum = {
    ...vab,
    rpcUrls: {
        ...vab.rpcUrls,
        alchemy: {
            http: ["https://arb-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const arbitrumGoerli = {
    ...vabg,
    rpcUrls: {
        ...vabg.rpcUrls,
        alchemy: {
            http: ["https://arb-goerli.g.alchemy.com/v2"],
        },
    },
};
export const arbitrumSepolia = {
    ...vabs,
    rpcUrls: {
        ...vabs.rpcUrls,
        alchemy: {
            http: ["https://arb-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const goerli = {
    ...vgo,
    rpcUrls: {
        ...vgo.rpcUrls,
        alchemy: {
            http: ["https://eth-goerli.g.alchemy.com/v2"],
        },
    },
};
export const mainnet = {
    ...vmain,
    rpcUrls: {
        ...vmain.rpcUrls,
        alchemy: {
            http: ["https://eth-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const optimism = {
    ...vop,
    rpcUrls: {
        ...vop.rpcUrls,
        alchemy: {
            http: ["https://opt-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const optimismGoerli = {
    ...vopg,
    rpcUrls: {
        ...vopg.rpcUrls,
        alchemy: {
            http: ["https://opt-goerli.g.alchemy.com/v2"],
        },
    },
};
export const optimismSepolia = {
    ...vops,
    rpcUrls: {
        ...vops.rpcUrls,
        alchemy: {
            http: ["https://opt-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const sepolia = {
    ...vsep,
    rpcUrls: {
        ...vsep.rpcUrls,
        alchemy: {
            http: ["https://eth-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const base = {
    ...vbase,
    rpcUrls: {
        ...vbase.rpcUrls,
        alchemy: {
            http: ["https://base-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const baseGoerli = {
    ...vbaseg,
    rpcUrls: {
        ...vbaseg.rpcUrls,
        alchemy: {
            http: ["https://base-goerli.g.alchemy.com/v2"],
        },
    },
};
export const baseSepolia = {
    ...vbases,
    rpcUrls: {
        ...vbases.rpcUrls,
        alchemy: {
            http: ["https://base-sepolia.g.alchemy.com/v2"],
        },
    },
};
export const polygonMumbai = {
    ...vpgm,
    rpcUrls: {
        ...vpgm.rpcUrls,
        alchemy: {
            http: ["https://polygon-mumbai.g.alchemy.com/v2"],
        },
    },
};
export const polygonAmoy = {
    ...vpga,
    rpcUrls: {
        ...vpga.rpcUrls,
        alchemy: {
            http: ["https://polygon-amoy.g.alchemy.com/v2"],
        },
    },
};
export const polygon = {
    ...vpg,
    rpcUrls: {
        ...vpg.rpcUrls,
        alchemy: {
            http: ["https://polygon-mainnet.g.alchemy.com/v2"],
        },
    },
};
export const fraxtal = {
    ...vfrax,
    rpcUrls: {
        ...vfrax.rpcUrls,
    },
};
export const fraxtalSepolia = defineChain({
    id: 2523,
    name: "Fraxtal Sepolia",
    nativeCurrency: { name: "Frax Ether", symbol: "frxETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://rpc.testnet-sepolia.frax.com"],
        },
    },
});
export const zora = {
    ...vzora,
    rpcUrls: {
        ...vzora.rpcUrls,
    },
};
export const zoraSepolia = {
    ...vzoras,
    rpcUrls: {
        ...vzoras.rpcUrls,
    },
};
export const worldChainSepolia = defineChain({
    id: 4801,
    name: "World Chain Sepolia",
    network: "World Chain Sepolia",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://worldchain-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://worldchain-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://worldchain-sepolia.g.alchemy.com/v2"],
        },
    },
});
export const worldChain = defineChain({
    id: 480,
    name: "World Chain",
    network: "World Chain",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://worldchain-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://worldchain-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://worldchain-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const shapeSepolia = defineChain({
    id: 11011,
    name: "Shape Sepolia",
    network: "Shape Sepolia",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://shape-sepolia.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://shape-sepolia.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://shape-sepolia.g.alchemy.com/v2"],
        },
    },
});
export const shape = defineChain({
    id: 360,
    name: "Shape",
    network: "Shape",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: {
        default: {
            http: ["https://shape-mainnet.g.alchemy.com/v2"],
        },
        public: {
            http: ["https://shape-mainnet.g.alchemy.com/v2"],
        },
        alchemy: {
            http: ["https://shape-mainnet.g.alchemy.com/v2"],
        },
    },
});
export const arbitrumNova = {
    ...vabn,
    rpcUrls: {
        ...vabn.rpcUrls,
    },
};
//# sourceMappingURL=chains.js.map